import { Cancel, CheckCircle, SwapHoriz } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import voucherServices from '../voucherServices';
import { useAlert } from '../../../../../../context/AlertContext';

/**
 * GenerateCDNoteVoucher
 * @param {Object} sourceVoucher - The original Invoice/Purchase object
 * @param {String} noteType - "Credit Note" or "Debit Note"
 */
function GenerateCDNoteVoucher({ sourceVoucher, noteType }) {
    const navigate = useNavigate();
    const { AppAlert } = useAlert();
    const [dialogStatus, setDialogStatus] = useState(false);
    const [loading, setLoadingStatus] = useState(false);

    const handleAction = () => {
        setLoadingStatus(true);

        // 1. Map Source Invoice to New Note Payload
        const payload = {
            voucherType: noteType,
            status: 1, // Draft
            referenceId: sourceVoucher.id, // Linking to original invoice
            partyId: sourceVoucher.partyId,
            ledgerId: sourceVoucher.ledgerId,
            paymentLedgerId: sourceVoucher.paymentLedgerId,
            
            // Financial Totals (Starting as full return, user can edit later)
            subTotal: sourceVoucher.subTotal,
            taxAmount: sourceVoucher.taxAmount,
            grandTotal: sourceVoucher.grandTotal,
            
            // Address Inheritance
            shippingAddressId: sourceVoucher.shippingAddressId,
            billingAddressId: sourceVoucher.billingAddressId,
            
            // Tax Metadata
            partyGstin: sourceVoucher.partyGstin,
            partyPan: sourceVoucher.partyPan,
            
            narration: `Generated against ${sourceVoucher.voucherType} #${sourceVoucher.voucherNo || sourceVoucher.id}`
        };

        // 2. Save via existing Voucher Controller
        voucherServices.save(payload).then(res => {
            if (!res.error) {
                AppAlert(`${noteType} Draft Created`);
                // Redirect to the specific details page (We will build these next)
                const routePath = noteType === "Credit Note" ? "credit-note" : "debit-note";
                navigate(`/voucher/cd-note/details/${res.id}`);
                setDialogStatus(false);
            } else {
                AppAlert(res.msg, "error");
            }
        }).catch(error => {
            console.error(error);
            AppAlert("Failed to generate voucher", "error");
        }).finally(() => {
            setLoadingStatus(false);
            setDialogStatus(false);
        });
    };

    return (
        <>
            {/* The Trigger Button */}
            <button 
                className={`btn btn-outline-${noteType === 'Credit Note' ? 'danger' : 'warning'} ms-2`} 
                onClick={() => setDialogStatus(true)}
            >
                <SwapHoriz /> Generate {noteType}
            </button>

            {/* Confirmation Modal */}
            <Dialog
                open={dialogStatus}
                onClose={() => !loading && setDialogStatus(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle className='fw-bold'>
                    Confirm {noteType} Generation
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <div className='text-center p-4'>
                            <CircularProgress />
                            <div className='mt-2'>Creating Draft Voucher...</div>
                        </div>
                    ) : (
                        <div className='p-2'>
                            <p>You are about to create a <strong>{noteType}</strong> linked to this invoice.</p>
                            <ul className='small text-muted'>
                                <li>Party: {sourceVoucher.partyName || 'Linked Customer/Vendor'}</li>
                                <li>Reference: #{sourceVoucher.voucherNo || sourceVoucher.id}</li>
                                <li>The stock and ledgers will <strong>not</strong> be affected until you finalize the note.</li>
                            </ul>
                        </div>
                    )}
                </DialogContent>
                
                {!loading && (
                    <DialogActions className='p-3'>
                        <button className='btn btn-light' onClick={() => setDialogStatus(false)}>
                            <Cancel /> Cancel
                        </button>
                        <button className='btn btn-primary' onClick={handleAction}>
                            Confirm & Create <CheckCircle className='ms-1' />
                        </button>
                    </DialogActions>
                )}
            </Dialog>
        </>
    );
}

export default GenerateCDNoteVoucher;